import { Button, Typography } from '@ag1/ui';
import clsx from 'clsx';
import { usePageContext } from 'processes/Page/PageContext';
import { useMemo, useRef } from 'react';
import { useLocale } from 'shared/hooks/useLocale';
import UnifiedButton from 'shared/studio/UnifiedButton/UnifiedButton';
import { type UnifiedButtonType } from 'shared/studio/UnifiedButton/UnifiedButton.types';
import { getSanityLinkUrl } from 'shared/utils/getSanityLinkUrl';
import { HeroFullV4 as HeroFullV4Props, SanityKeyed } from 'types/studio/studio';
import { useGoToOffers } from '../../hooks/useGoToOffers';
import CustomPortableText from '../PortableText/PortableText';
import { StudioBaseIconV2 } from '../StudioBaseIcon/StudioBaseIcon';
import StudioResponsiveMedia from '../StudioResponsiveMedia/StudioResponsiveMedia';
import style from './HeroFullV4.module.scss';
import { CommonSectionProps } from '../types';

const HeroFullV4 = (props: SanityKeyed<HeroFullV4Props> & CommonSectionProps) => {
  const {
    _key: key,
    _type: sectionType,
    logo,
    headline,
    subtitle,
    switchButton,
    baseButton,
    baseButtonLink,
    heroMedia,
    layoutType,
    hugPadding,
    additionalButtonSwitch,
    additionalButton,
    contentBelowImage,
    theme,
    unifiedButton,
    visibleOnPageLoad
  } = props;

  const {
    disablePrimaryCTAButtons,
    shouldDisableUnifiedButton,
    shouldDisableCTALink,
    maybeReplaceOfferlessCheckoutLink
  } = usePageContext();
  const { goToOffer } = useGoToOffers({ withScroll: true });

  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleButtonClick = () => {
    if (disablePrimaryCTAButtons) {
      return;
    }
    goToOffer();
    if (buttonRef?.current) {
      buttonRef.current.blur();
    }
  };

  const locale = useLocale();

  const buttonLink = useMemo(
    () => maybeReplaceOfferlessCheckoutLink(getSanityLinkUrl(baseButtonLink?.btnLink, locale)),
    [maybeReplaceOfferlessCheckoutLink, baseButtonLink?.btnLink, locale]
  );

  const additionalButtonLink = useMemo(
    () => maybeReplaceOfferlessCheckoutLink(getSanityLinkUrl(additionalButton?.btnLink, locale)),
    [maybeReplaceOfferlessCheckoutLink, additionalButton?.btnLink, locale]
  );

  const disableUnifiedButton = useMemo(
    () => shouldDisableUnifiedButton(unifiedButton),
    [shouldDisableUnifiedButton, unifiedButton]
  );

  return (
    <div
      className={clsx(
        style.container,
        style[`container-layout-${layoutType}`],
        contentBelowImage && style.contentBelowOnMobile
      )}
      id={key}>
      <StudioResponsiveMedia
        {...heroMedia}
        className={clsx(style.media, contentBelowImage && style.contentBelowOnMobile)}
        eagerLoading={visibleOnPageLoad}
        sizes="100vw"
      />
      <div
        className={clsx(
          style.content,
          style[`content-layout-${layoutType}`],
          {
            [style[`content-padding-${hugPadding}`]]: layoutType === 'hug'
          },
          contentBelowImage && style.contentBelowOnMobile
        )}>
        {logo?.asset && (
          <StudioBaseIconV2 className={style.logo} icon={logo} eagerLoading={visibleOnPageLoad} />
        )}

        <div className={clsx(style.wrap, contentBelowImage && style.contentBelowOnMobile)}>
          <Typography
            variant="headline"
            size="1"
            color="brand-white"
            noPadding
            className={clsx(style.headline, contentBelowImage && style.typoHeadline)}>
            <CustomPortableText value={headline} tag="h1" />
          </Typography>
          {subtitle && (
            <Typography
              variant="subtitle"
              size="1"
              noPadding
              weight="700"
              color="brand-white"
              className={clsx(contentBelowImage && style.typoSubtitle)}>
              <CustomPortableText value={subtitle} tag="h2" />
            </Typography>
          )}
        </div>
        <div className={style.buttonWrapper}>
          {unifiedButton && (
            <UnifiedButton
              className={clsx(style.button, contentBelowImage && style.contentBelowOnMobile)}
              size={{ l: 'large', xxs: 'medium' }}
              sectionType={sectionType}
              theme={theme}
              unifiedButton={unifiedButton as UnifiedButtonType}
              isDisabled={disableUnifiedButton}
            />
          )}
          {!unifiedButton && switchButton === 'button' && baseButton && (
            <Button
              ref={buttonRef}
              className={clsx(style.button, contentBelowImage && style.contentBelowOnMobile)}
              onClick={handleButtonClick}
              disabled={disablePrimaryCTAButtons}
              size={{ l: 'large', xxs: 'medium' }}
              variant={baseButton.variant}
              rounded
              icon={baseButton.showIcon && baseButton.iconOptions?.icon}
              iconPosition={baseButton.iconOptions?.position}
              theme={theme}>
              {baseButton.portableText && <CustomPortableText value={baseButton.portableText} />}
            </Button>
          )}
          {!unifiedButton && switchButton === 'link' && baseButtonLink && buttonLink && (
            <Button
              className={clsx(style.button, contentBelowImage && style.contentBelowOnMobile)}
              size={{ l: 'large', xxs: 'medium' }}
              href={buttonLink}
              disabled={shouldDisableCTALink(buttonLink)}
              variant={baseButtonLink.variant}
              rounded
              icon={baseButtonLink.showIcon && baseButtonLink.iconOptions?.icon}
              iconPosition={baseButtonLink.iconOptions?.position}
              theme={theme}>
              {baseButtonLink.portableText && (
                <CustomPortableText value={baseButtonLink.portableText} />
              )}
            </Button>
          )}
          {additionalButtonSwitch && additionalButton && additionalButtonLink && (
            <Button
              className={clsx(
                style.button,
                style.additionalButton,
                contentBelowImage && style.contentBelowOnMobile
              )}
              size={{ l: 'large', xxs: 'medium' }}
              href={additionalButtonLink}
              disabled={shouldDisableCTALink(additionalButtonLink)}
              variant={additionalButton.variant}
              rounded
              icon={additionalButton.showIcon && additionalButton.iconOptions?.icon}
              iconPosition={additionalButton.iconOptions?.position}
              theme={theme}>
              {additionalButton.portableText && (
                <CustomPortableText value={additionalButton.portableText} />
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroFullV4;
